/**
 * BottomSheetTabs — v3 Excel Worksheet Navigation
 *
 * 6-tab bottom bar mirroring Excel's worksheet tab paradigm.
 * Shares the canvas gradient — transparent bg, separated by 1px divider.
 *
 * Spec: docs/superpowers/specs/2026-03-17-layout-information-density-design.md §1.4
 *
 * Desktop (≥1280px): 34px, text-only labels per spec parenthetical "no icons in current mockup"
 * Tablet (768-1279px): 34px, icon + label (extrapolation — spec only defines mobile/desktop)
 * Mobile (<768px): 40px, icons only for inactive, icon + label for active, horizontally scrollable
 *
 * testid note: `mobile-tab-bar` shared with sibling MobileTabBarV2 — safe because only
 * one is mounted at a time (via isV3() version gate in MobileTabBar.tsx).
 */

import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  FileText,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  PieChart,
} from 'lucide-react';

interface BottomTab {
  key: string;
  label: string;
  icon: typeof BarChart3;
  path: string;
}

const TABS: BottomTab[] = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/dashboard' },
  { key: 'policies', label: 'Policies', icon: FileText, path: '/policies' },
  { key: 'findings', label: 'Findings', icon: AlertTriangle, path: '/findings' },
  { key: 'var', label: 'VAR', icon: TrendingUp, path: '/var' },
  { key: 'renewals', label: 'Renewals', icon: RefreshCw, path: '/renewals' },
  { key: 'portfolio', label: 'Portfolio', icon: PieChart, path: '/portfolio-analytics' },
];

export function BottomSheetTabs() {
  const location = useLocation();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav
      data-testid="mobile-tab-bar"
      data-version="v3"
      aria-label="Main navigation"
      className="fixed bottom-0 left-0 w-full z-40 font-system bg-[#d8d3cc] dark:bg-[#1a1f2e]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Top zone divider — spec §5.1 */}
      <div
        aria-hidden="true"
        className="h-px bg-[rgba(0,0,0,0.1)] dark:bg-[rgba(255,255,255,0.08)]"
      />

      {/* Tab container — transparent (canvas gradient shows through), 34px desktop / 40px mobile.
          overscroll-contain prevents horizontal rubber-band leak on iOS Safari / Chrome Android. */}
      <div className="flex items-stretch h-10 xl:h-[34px] overflow-x-auto overflow-y-hidden scrollbar-hide [overscroll-behavior-x:contain]">
        {TABS.map((tab, index) => {
          const active = isActive(tab.path);
          const Icon = tab.icon;
          const isLast = index === TABS.length - 1;

          return (
            <Link
              key={tab.key}
              to={tab.path}
              data-testid={`tab-${tab.key}`}
              aria-current={active ? 'page' : undefined}
              className={[
                // Layout
                'relative flex items-center justify-center gap-1.5',
                'flex-1 min-w-0 px-2 md:px-3',
                'select-none whitespace-nowrap',
                'transition-colors duration-150',
                // Typography: whisper-sm (11px), weight via active state
                'text-whisper-sm text-etched',
                active ? 'font-semibold' : 'font-normal',
                // Active state bg — spec §1.4 active tab
                active
                  ? 'bg-[rgba(255,255,255,0.35)] dark:bg-[rgba(2,6,23,0.35)]'
                  : 'bg-transparent',
                // Text color — primary (active) or secondary (inactive) per spec §4.3
                active
                  ? 'text-t1'
                  : 'text-t2',
                // Tab separator (right border, except last tab) — spec §1.4
                !isLast
                  ? 'border-r border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.05)]'
                  : '',
              ].join(' ')}
            >
              {/* Icon: visible mobile (<768px) and tablet (<1280px). Hidden on desktop ≥1280px per spec parenthetical.
                  20px size with 1.5px stroke — thumb-friendly on mobile 40px target, matches toolbar visual mass. */}
              <Icon
                size={20}
                strokeWidth={1.5}
                className="xl:hidden flex-shrink-0"
                aria-hidden="true"
              />

              {/* Label: always visible on active tab (with truncate guard). Hidden on mobile inactive (icons-only). Tablet+: all visible. */}
              <span className={active ? 'truncate' : 'hidden md:inline truncate'}>
                {tab.label}
              </span>
            </Link>
          );
        })}

        {/* TODO: Settings cog (spec §1.4 line 177 — deferred to implementation, no detailed spec yet).
            Should be right-aligned after last tab, separated by tab divider. */}
      </div>
    </nav>
  );
}
